import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import { Box, Button, IconButton, Link as MuiLink, Typography, Select, MenuItem, FormControl } from '@mui/material';
import { Breadcrumb } from 'src/components/Breadcrumb';
import { GroupOptionLabel } from 'src/components/GroupOptionLabel';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React, { FC, useMemo, useCallback } from 'react';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import { StatusChip } from 'src/UIKit';
import { TableHeader, TableHeaderItem, TABLE_HEADER_ACTION_SX, TABLE_HEADER_ACTIONS_WRAP_SX } from 'src/components/TableHeader/TableHeader';
import { MountingComponent } from 'src/components/MountingComponent';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { formatDateWithoutTimezone } from 'src/constants/helpers';
import { GroupFormDialog } from 'src/modules/groups/components';
import { DynamicGroupRulesFormDialog } from 'src/modules/groups/components/DynamicGroupRulesForm';
import { Group } from 'src/modules/groups/types';
import { useSpamContactDialog } from 'src/modules/inbox/components';
import {
  useContactsViewController,
  useCreateContactModalController,
  useEditContactDrawerController,
} from '../../controllers';
import { Contact, ContactValidStatus } from '../../types';
import { AddContactsToGroupDialog } from '../AddContactsToGroupDialog';
import { CreateContactModal } from '../CreateContactModal';
import { EditContactDrawer } from '../EditContactDrawer';

type Props = {
  groupId?: string;
  isSpam?: boolean;
  isInvalidContacts?: boolean;
  isNew?: boolean;
  openDynamicRules?: boolean;
  embedded?: boolean;
  isLoading?: boolean;
};

const ContactsViewComponent: FC<Props> = ({
  groupId,
  isSpam,
  isInvalidContacts,
  isNew,
  openDynamicRules,
  embedded = false,
  isLoading = false,
}) => {
  const {
    isPageLoading,
    currentCompanyId,
    groups,
    group,
    users,
    customFields,
    initialColumnVisibility,
    contactsFilter,
    contactList,
    reloadContacts,
    changeContactFilterForm,
    changeContactsSearchFilter,
    changeContactsSorting,
    changeContactsColumnVisibility,
    changeContactsPagination,
    removeContact,
    groupFormDialogController,
    addContactsToGroupDialogController,
    dynamicGroupRulesFormDialogController,
  } = useContactsViewController({ groupId, isSpam, isInvalidContacts, isNew, openDynamicRules });
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const { SpamContactDialog, SpamButton } = useSpamContactDialog();

  const createContactModalController = useCreateContactModalController();
  const editContactDrawerController = useEditContactDrawerController(reloadContacts);

  // Memoize loading states to prevent unnecessary recalculations
  const isDataLoading = useMemo(
    () => (embedded ? isLoading : isPageLoading),
    [embedded, isLoading, isPageLoading],
  );
  const isAllDataLoading = useMemo(() => {
    // Check if any critical data is still loading
    return !customFields || !contactList || !groups || !users || isDataLoading;
  }, [customFields, contactList, groups, users, isDataLoading]);

  // Memoize the columns configuration to prevent recreating on every render
  const columnsConfig = useMemo((): (GridColDef<Contact> & { initVisibility?: boolean })[] => [
      {
        field: 'name',
        headerName: 'Name',
        renderCell: (r: GridRenderCellParams<Contact>) => {
          return (
            <>
              {r.row.firstName} {r.row.lastName}
            </>
          );
        },
        minWidth: 200,
        editable: false,
        sortable: true,
        flex: 1,
        initVisibility: true,
      },
      {
        field: 'phoneNumber',
        headerName: 'Phone Number',
        minWidth: 100,
        editable: false,
        sortable: true,
        flex: 1,
        initVisibility: true,
      },
      {
        field: 'validStatus',
        headerName: 'Status',
        renderCell: (r: GridRenderCellParams<Contact>) => {
          if (r.row.validStatus == 'invalid') {
            return <StatusChip label="Invalid" />;
          }

          if (r.row.optOutDate) {
            return (
              <StatusChip label="Opted Out" tone="brandDark" />
            );
          }

          return (
            <StatusChip label="Active" tone="brandLight" />
          );
        },
        minWidth: 100,
        editable: false,
        sortable: true,
        flex: 1,
        initVisibility: true,
      },
      {
        field: 'email',
        headerName: 'Email',
        renderCell: (r: GridRenderCellParams<Contact>) => {
          return (
            <MuiLink href={`mailto:${r.row.email}`} style={{ display: 'inline-block' }}>
              <Typography variant="caption" color="primary">
                {r.row.email}
              </Typography>
            </MuiLink>
          );
        },
        minWidth: 100,
        editable: false,
        sortable: true,
        flex: 1,
        initVisibility: true,
      },
      {
        field: 'birthdate',
        headerName: 'Birthdate',
        renderCell: (r: GridRenderCellParams<Contact>) => {
          return r.row.birthdate ? formatDateWithoutTimezone(r.row.birthdate) : '-';
        },
        minWidth: 100,
        editable: false,
        sortable: true,
        flex: 1,
        initVisibility: true,
      },
      {
        field: 'timeZone',
        headerName: 'Timezone',
        minWidth: 100,
        editable: false,
        sortable: true,
        flex: 1,
        initVisibility: true,
      },
      {
        field: 'optInType',
        headerName: 'Opt In Type',
        minWidth: 100,
        initVisibility: false,
        editable: false,
        sortable: true,
        flex: 1,
      },
      ...(customFields
        ?.filter((c): c is typeof c & { metadataId: string } => c.metadataId != null)
        .map(c => ({
          field: c.metadataId,
          headerName: c.description,
          renderCell: (r: GridRenderCellParams<Contact>) => {
            const data = r.row.metadata?.find(d => d.metadataId === c.metadataId);

            if (!data?.value) return '';

            if (c.type === 'date') {
              return formatDateWithoutTimezone(data.value);
            }

            return data.value;
          },
          minWidth: 100,
          initVisibility: false,
          editable: false,
          sortable: true,
          flex: 1,
        })) || []),
    ].map(column =>
      initialColumnVisibility && column.field != null && column.field in initialColumnVisibility
        ? { ...column, initVisibility: initialColumnVisibility[column.field] }
        : column,
    ),
    [customFields, initialColumnVisibility],
  );

  // Memoize the render action callback to prevent unnecessary re-renders
  const renderAction = useCallback(
    (r: Contact) => {
      return (
        <>
          {r.isSpam?.value ? (
            <SpamButton
              selectedCompanyId={currentCompanyId!}
              contactId={r.id}
              isSpam
              nextAction={reloadContacts}
              size="small"
            />
          ) : (
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                editContactDrawerController.open({
                  contactData: r,
                  customFields: customFields || [],
                  groups,
                  users,
                })
              }
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
          )}

          {!r.isSpam?.value && group?.type !== 'dynamic' && (
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                openDialog({
                  title: `Remove ${
                    r.firstName || r.lastName
                      ? `"${r.firstName} ${r.lastName}"`
                      : `"${r.phoneNumber}"`
                  }?`,
                  content: `Are you sure you want to remove this contact ${
                    group?.type === 'default' ? '' : 'from this group'
                  }?`,
                  onConfirm: () => removeContact(r.id),
                })
              }
            >
              {group?.type === 'default' ? (
                <DeleteIcon fontSize="inherit" />
              ) : (
                <RemoveCircleOutlineIcon fontSize="inherit" />
              )}
            </IconButton>
          )}
        </>
      );
    },
    [
      currentCompanyId,
      reloadContacts,
      editContactDrawerController,
      customFields,
      groups,
      users,
      group,
      openDialog,
      removeContact,
      SpamButton,
    ],
  );

  // Memoize the common header JSX to prevent recreating on each render
  const headerJSX = useMemo(
    () => (
      <TableHeader
        searchValue={contactsFilter.query}
        onSearchChange={changeContactsSearchFilter}
        filters={
          <TableHeaderItem>
            <FormControl fullWidth>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={contactsFilter.validStatus || ''}
                onChange={e =>
                  changeContactFilterForm({
                    validStatus: (e.target.value as ContactValidStatus) || undefined,
                  })
                }
              >
                <MenuItem value="">All Contacts</MenuItem>
                <MenuItem value="valid">Active</MenuItem>
                <MenuItem value="invalid">Invalid</MenuItem>
                <MenuItem value="opted out">Opted-Out</MenuItem>
              </Select>
            </FormControl>
          </TableHeaderItem>
        }
        actions={
          !!group && (
            <Box sx={TABLE_HEADER_ACTIONS_WRAP_SX}>
              <Button
                onClick={() => groupFormDialogController.open(group)}
                disabled={group?.type === 'default'}
                variant="contained"
                sx={TABLE_HEADER_ACTION_SX}
              >
                Edit Group
              </Button>

              {group?.type === 'custom' && (
                <Button
                  onClick={() => addContactsToGroupDialogController.open(group.id)}
                  variant="contained"
                  sx={TABLE_HEADER_ACTION_SX}
                >
                  Add Contacts
                </Button>
              )}

              {group?.type === 'dynamic' && (
                <Button
                  onClick={() => dynamicGroupRulesFormDialogController.open(group)}
                  variant="contained"
                  sx={TABLE_HEADER_ACTION_SX}
                >
                  Edit Rules
                </Button>
              )}
            </Box>
          )
        }
      />
    ),
    [
      contactsFilter.query,
      contactsFilter.validStatus,
      changeContactsSearchFilter,
      changeContactFilterForm,
      group,
      groupFormDialogController,
      addContactsToGroupDialogController,
      dynamicGroupRulesFormDialogController,
    ],
  );

  // Memoize sorting configuration
  const sortingConfig = useMemo(
    () => ({
      value: contactsFilter.sortMetadataId
        ? [{ field: contactsFilter.sortMetadataId, sort: contactsFilter.sortOrder || 'asc' }]
        : contactsFilter.sort,
      onChange: changeContactsSorting,
    }),
    [
      contactsFilter.sort,
      contactsFilter.sortMetadataId,
      contactsFilter.sortOrder,
      changeContactsSorting,
    ],
  );

  // Memoize pagination configuration for main grid
  const paginationConfig = useMemo(
    () => ({
      page: contactsFilter.page,
      pageSize: contactsFilter.limit,
      totalCount: contactList?.count,
      onChange: changeContactsPagination,
    }),
    [contactsFilter.page, contactsFilter.limit, contactList?.count, changeContactsPagination],
  );

  const gridDataComponent = (
    <GridData
      key={`contacts-grid-${groupId || 'default'}-${embedded ? 'embedded' : 'standalone'}`}
      header={headerJSX}
      isLoading={isAllDataLoading}
      rows={isAllDataLoading ? [] : contactList?.items}
      columns={columnsConfig}
      renderAction={renderAction}
      onColumnVisibilityModelChange={changeContactsColumnVisibility}
      sorting={sortingConfig}
      pagination={paginationConfig}
    />
  );

  return (
    <>
      <ConfirmationDialog />
      <SpamContactDialog />
      <GroupFormDialog
        {...groupFormDialogController}
        onAddContacts={() =>
          groupId && addContactsToGroupDialogController.open(groupId, group?.name)
        }
        onEditRules={() => group && dynamicGroupRulesFormDialogController.open(group)}
      />
      <AddContactsToGroupDialog
        {...addContactsToGroupDialogController}
        onSuccess={reloadContacts}
      />
      <DynamicGroupRulesFormDialog
        {...dynamicGroupRulesFormDialogController}
        onSuccess={reloadContacts}
      />

      <MountingComponent conditions={[createContactModalController.show]}>
        <CreateContactModal {...createContactModalController} refresh={reloadContacts} />
      </MountingComponent>

      <EditContactDrawer {...editContactDrawerController} refreshContacts={reloadContacts} />

      {embedded ? (
        gridDataComponent
      ) : (
        <>
          <PrivateLayoutView title={<Title group={group} isSpam={isSpam} isInvalidContacts={isInvalidContacts} />}>
            {gridDataComponent}
          </PrivateLayoutView>
        </>
      )}
    </>
  );
};

ContactsViewComponent.displayName = 'ContactsView';

export const ContactsView = React.memo(ContactsViewComponent);

const TitleComponent: FC<{
  group?: Group | null;
  isSpam?: boolean | null;
  isInvalidContacts?: boolean | null;
}> = ({ group, isSpam, isInvalidContacts }) => {
  if (isSpam) {
    return (
      <Breadcrumb
        items={[
          { label: 'Contact Groups', path: '/groups' },
          { label: 'Spam Contacts', isActive: true },
        ]}
      />
    );
  }

  if (isInvalidContacts) {
    return (
      <Breadcrumb
        items={[
          { label: 'Contact Groups', path: '/groups' },
          { label: 'Invalid Contacts', isActive: true },
        ]}
      />
    );
  }

  if (!group) {
    return null;
  }

  if (group.type === 'default' || group.name?.toLowerCase().includes('all contacts')) {
    return (
      <Breadcrumb
        items={[
          { label: 'Contact Groups', path: '/groups' },
          { label: <GroupOptionLabel name="All Contacts" validContactCount={group.validContactCount} />, isActive: true },
        ]}
      />
    );
  }

  return (
    <Breadcrumb
      items={[
        { label: 'Contact Groups', path: '/groups' },
        { label: <GroupOptionLabel name={group.name || ''} validContactCount={group.validContactCount} />, isActive: true },
      ]}
    />
  );
};

const Title = React.memo(TitleComponent);
